import type { EnhancedFileCitation } from '../../types/chat.types';

type CitationGroup = {
  ids: string[];
  position: number;
};

type ComposeOptions = {
  finalize?: boolean;
};

const toSafeIndex = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  if (value < 0) {
    return 0;
  }

  return Math.floor(value);
};

const needsLeadingSpace = (text: string, position: number): boolean => {
  if (position === 0) {
    return false;
  }

  return !/\s/.test(text[position - 1]);
};

const formatCitationMarker = (ids: string[]): string =>
  `<cite ids="${ids.join(',')}" />`;

const dedupeAndSort = (
  citations: EnhancedFileCitation[],
): EnhancedFileCitation[] => {
  const byKey = new Map<string, EnhancedFileCitation>();

  citations.forEach(citation => {
    if (!citation.file_id) {
      return;
    }

    const key = [
      citation.file_id,
      toSafeIndex(citation.index ?? 0),
      citation.annotation_index ?? '',
    ].join(':');
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...citation,
        index: toSafeIndex(citation.index ?? 0),
      });
      return;
    }

    byKey.set(key, {
      ...existing,
      ...citation,
      index: toSafeIndex(citation.index ?? existing.index ?? 0),
    });
  });

  return Array.from(byKey.values()).sort((a, b) => {
    const byIndex = (a.index ?? 0) - (b.index ?? 0);
    if (byIndex !== 0) {
      return byIndex;
    }
    return a.file_id.localeCompare(b.file_id);
  });
};

const groupCitationsByPosition = (
  citations: EnhancedFileCitation[],
): CitationGroup[] => {
  if (citations.length === 0) {
    return [];
  }

  const groupsByPosition = new Map<number, Set<string>>();

  citations.forEach(citation => {
    const index = toSafeIndex(citation.index ?? 0);
    const id = citation.file_id;
    const set = groupsByPosition.get(index) ?? new Set<string>();
    set.add(id);
    groupsByPosition.set(index, set);
  });

  return Array.from(groupsByPosition.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([position, ids]) => ({
      position,
      ids: Array.from(ids),
    }));
};

const injectGroupMarkers = (
  text: string,
  groups: CitationGroup[],
  finalize: boolean,
): { content: string; pendingGroups: CitationGroup[] } => {
  const pendingGroups: CitationGroup[] = [];
  const insertions = groups
    .map(group => {
      const safeIndex = toSafeIndex(group.position);
      if (!finalize && safeIndex > text.length) {
        pendingGroups.push(group);
        return null;
      }

      return {
        ids: group.ids,
        position: Math.min(safeIndex, text.length),
      };
    })
    .filter((value): value is { ids: string[]; position: number } =>
      Boolean(value),
    )
    .sort((a, b) => b.position - a.position);

  let content = text;

  insertions.forEach(insertion => {
    const marker = formatCitationMarker(insertion.ids);
    const prefix = needsLeadingSpace(content, insertion.position) ? ' ' : '';
    const suffix = /\s/.test(content[insertion.position] ?? '') ? '' : ' ';
    content =
      content.slice(0, insertion.position) +
      prefix +
      marker +
      suffix +
      content.slice(insertion.position);
  });

  return { content, pendingGroups };
};

const normalizeCitationPlacementNearCodeFence = (content: string): string => {
  // Move cite markers to their own line when they appear after a CLOSING code fence.
  //
  // This function distinguishes between opening and closing fences by tracking
  // code block state as it processes the content line by line.
  //
  // Opening fence: starts a code block (may have language identifier)
  // Closing fence: ends a code block (no language identifier)
  //
  // We only want to normalize citations after CLOSING fences, not opening ones.
  // Normalizing after an opening fence would put the citation inside the code block.

  const lines = content.split('\n');
  let inCodeBlock = false;
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Match a fence line: optional indent, ```, optional content after
    const fenceMatch = line.match(/^([ \t]*)(```)(.*)$/);

    if (!fenceMatch) {
      // Not a fence line, pass through unchanged
      result.push(line);
      continue;
    }

    const [, indent, fence, afterFence] = fenceMatch;

    if (!inCodeBlock) {
      // This is an OPENING fence - don't modify it, just toggle state
      inCodeBlock = true;
      result.push(line);
      continue;
    }

    // This is a CLOSING fence - check if there are citations that need normalization
    inCodeBlock = false;

    // Check if afterFence contains citation markers that should be moved to their own line
    const citationPattern = /^[ \t]+((?:<cite ids="[^"]+" \/>[ \t]*)+)$/;
    const citationMatch = afterFence.match(citationPattern);

    if (!citationMatch) {
      // No citations to normalize, pass through unchanged
      result.push(line);
      continue;
    }

    // Move citations to their own line, preserving indentation
    const citations = citationMatch[1].trim();
    result.push(`${indent}${fence}`);
    result.push(`${indent}${citations}`);
  }

  return result.join('\n');
};

export const composeAnnotatedContent = (
  text: string,
  citations: EnhancedFileCitation[],
  options: ComposeOptions = {},
): {
  content: string;
  grouped: CitationGroup[];
  pendingGroups: CitationGroup[];
  resolvedCitations: EnhancedFileCitation[];
} => {
  const normalized = dedupeAndSort(citations);
  const grouped = groupCitationsByPosition(normalized);
  const { content, pendingGroups } = injectGroupMarkers(
    text,
    grouped,
    Boolean(options.finalize),
  );

  const resolvedIds = new Set(grouped.flatMap(group => group.ids));
  const resolvedCitations = normalized.filter(citation =>
    resolvedIds.has(citation.file_id),
  );

  return {
    content: normalizeCitationPlacementNearCodeFence(content),
    grouped,
    pendingGroups,
    resolvedCitations,
  };
};
